import React, { useEffect, useState } from 'react';
import { Alert, App, Button, Card, Col, DatePicker, Descriptions, Form, Input, Result, Row, Select, Space, Spin } from 'antd';
import { ArrowLeftOutlined, UserAddOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import RecruitPeoplePicker from './shared/RecruitPeoplePicker';
import { fmtDate, fmtMoney, recruitErrorMessage } from './shared/recruitUi';

/** Accepted offer → employee + Day-1 onboarding plan, in one transaction on the server. */
const OfferConvertPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: offer, isLoading } = useQuery({
    queryKey: hrmsKeys.offer(id || ''),
    queryFn: () => hrmsService.getOffer(id!),
    enabled: Boolean(id),
  });
  const { data: templates = [] } = useQuery({
    queryKey: hrmsKeys.onboardingTemplates(),
    queryFn: () => hrmsService.listOnboardingTemplates(),
  });

  useEffect(() => {
    if (!offer) return;
    form.setFieldsValue({
      email: offer.candidate_email || undefined,
      mobile: offer.candidate_phone || undefined,
      nationality: offer.candidate_nationality || undefined,
      join_date: offer.join_date ? dayjs(offer.join_date) : undefined,
    });
  }, [offer, form]);

  const convert = async () => {
    setError(null);
    let v: any;
    try {
      v = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      const r = await hrmsService.convertOffer(id!, {
        ...v,
        date_of_birth: dayjs(v.date_of_birth).format('YYYY-MM-DD'),
        join_date: v.join_date ? dayjs(v.join_date).format('YYYY-MM-DD') : undefined,
      });
      message.success(`Employee ${r?.employee?.employee_number || ''} created`);
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.offer(id!) });
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.offers() });
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.onboarding() });
      navigate(r?.onboarding_plan_id ? `/hr/onboarding/${r.onboarding_plan_id}` : `/hr/offers/${id}`);
    } catch (e: any) {
      setError(recruitErrorMessage(e, 'Conversion failed'));
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Convert to employee" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  if (!offer) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Convert to employee" />}>
        <Card>Not found</Card>
      </WorkspaceLayout>
    );
  }
  if (offer.status !== 'Accepted') {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Convert to employee" />}>
        <Result
          status="info"
          title={offer.status === 'Converted' ? 'Already converted' : `Offer is ${offer.status}`}
          subTitle={offer.status === 'Converted' ? `Employee ${offer.converted_employee_code || ''}` : 'Only accepted offers can be converted.'}
          extra={<Button onClick={() => navigate(`/hr/offers/${id}`)}>Back to offer</Button>}
        />
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Convert to employee"
          documentNumber={offer.offer_number}
          subtitle={`${offer.candidate_name} — ${offer.job_title}`}
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/hr/offers/${id}`)}>
                Cancel
              </Button>
              <Button type="primary" icon={<UserAddOutlined />} loading={saving} onClick={convert}>
                Create employee
              </Button>
            </Space>
          }
        />
      }
    >
      <div style={{ maxWidth: 900 }}>
        {error && <Alert type="error" showIcon closable onClose={() => setError(null)} style={{ marginBottom: 16 }} message="Could not create the employee" description={error} />}
        <Card size="small" title="From the offer" style={{ marginBottom: 16 }}>
          <Descriptions size="small" column={2}>
            <Descriptions.Item label="Candidate">{offer.candidate_number} — {offer.candidate_name}</Descriptions.Item>
            <Descriptions.Item label="Role">{offer.job_title}</Descriptions.Item>
            <Descriptions.Item label="Department">{offer.department_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Offered join date">{fmtDate(offer.join_date)}</Descriptions.Item>
            <Descriptions.Item label="Basic">{fmtMoney(offer.basic_salary, offer.currency)}</Descriptions.Item>
            <Descriptions.Item label="Probation">{offer.probation_months ?? 0} months</Descriptions.Item>
          </Descriptions>
        </Card>
        <Form form={form} layout="vertical">
          <Card title="Employee record" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="email" label="Work email" rules={[{ required: true, type: 'email', message: 'A valid work email is required' }]}>
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="mobile" label="Mobile">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="date_of_birth" label="Date of birth" rules={[{ required: true, message: 'Date of birth is required' }]}>
                  <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" disabledDate={(d) => d.isAfter(dayjs().subtract(16, 'year'))} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="gender" label="Gender" rules={[{ required: true, message: 'Gender is required' }]}>
                  <Select options={['Male', 'Female'].map((g) => ({ value: g, label: g }))} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="nationality" label="Nationality">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="emirates_id" label="Emirates ID / national ID">
                  <Input placeholder="784-XXXX-XXXXXXX-X" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="join_date" label="Actual join date">
                  <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item name="reporting_to_id" label="Reports to">
                  <RecruitPeoplePicker />
                </Form.Item>
              </Col>
            </Row>
          </Card>
          <Card title="Day-1 onboarding">
            <Form.Item
              name="onboarding_template_id"
              label="Onboarding checklist"
              extra="Leave empty to use the checklist set up for this position or department (otherwise the standard one). Tasks, owners and due dates can be adjusted on the onboarding plan."
            >
              <Select
                allowClear
                placeholder="Automatic — best match for position / department"
                options={templates.filter((t: any) => t.is_active !== false).map((t: any) => ({ value: t.id, label: t.template_name }))}
              />
            </Form.Item>
          </Card>
        </Form>
      </div>
    </WorkspaceLayout>
  );
};

export default OfferConvertPage;
